import { useQuery } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSessionStorage } from 'react-use';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { LlmModelData } from '../../../api/endpoints/llm';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { useResolve } from '../../Container';
import { AssistantContext } from './AssistantContext';
import { AssistantToolRegistry } from './AssistantToolRegistry';
import { ToolCallPart } from './ChatTypes';

export const useAssitantModels = () => {

    const [lastUsedModelId, setLastUsedModelId] = useSessionStorage('temp/modexl', '');
    const [model, setModel] = useState(null as LlmModelData | null);

    const api = useResolve(ApiClient);
    const query = useQuery({
        queryKey: ['llm.instances.list'],
        queryFn: async () => api.exec(v2.llm.instances.list()),
    });

    useEffect(() => {
        if (!query.data) return;
        // When data loads, set the model using the last used id
        const lum = query.data.find(x => x.id === lastUsedModelId);
        setModel(lum ?? query.data[0]);
    }, [query.data]);

    const switchModel = useCallback((next: LlmModelData) => {
        setLastUsedModelId(next.id);
        setModel(next);
    }, []);

    return {
        isLoading: query.isLoading,
        model,
        availableModels: query.data || [],
        switchModel,
    };
};

export const useSubmitMessage = () => {
    const { submitMessage } = useContext(AssistantContext);
    return submitMessage;
};

export const useSubmitToolResult = () => {
    const { submitToolResult } = useContext(AssistantContext);
    return submitToolResult;
};

export const useExecuteAssitantToolCall = () => {

    const dispatch = useResolve(CommandDispatcher);
    const registry = useResolve(AssistantToolRegistry);

    return useCallback((toolCall: ToolCallPart, onResult: (result: any) => void) => {
        const command = registry.get(toolCall.toolName)?.command;
        if (command) {
            const result = dispatch(command, [toolCall.args]);
            onResult(result);
        }
    }, []);
};

export const useAssitantToolCallResult = (toolCallId: string) => {

    const { messages } = useContext(AssistantContext);
    return useMemo(() => {

        for (const m of messages) {
            for (const p of m.content) {
                if (p.type === 'tool-result' && p.toolCallId === toolCallId) {
                    return p.result;
                }
            }
        }
        return null;

    }, [toolCallId, messages]);
};
